import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useMerchant } from '../../contexts/MerchantContext';
import authService from '../../services/authService';
import LoadingSpinner from '../LoadingSpinner';

const LineDirectLogin = () => {
  const { merchantSlug } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, loading: authLoading, user } = useAuth();
  const { merchant, loading: merchantLoading, error: merchantError, fetchMerchant } = useMerchant();
  const [status, setStatus] = useState('checking'); // checking, redirecting, error
  const [debugInfo, setDebugInfo] = useState({});

  useEffect(() => {
    console.log('🔄 LINE Direct Login - 開始檢查狀態');
    
    // 收集調試信息
    const info = {
      merchantSlug,
      isAuthenticated,
      authLoading,
      user: user ? { id: user.user_id, name: user.display_name } : null,
      merchant: merchant ? { id: merchant.id, name: merchant.name } : null,
      merchantLoading,
      merchantError,
      timestamp: new Date().toISOString()
    };
    setDebugInfo(info);
    console.log('🔍 LINE Direct Login Debug Info:', info);

    // 如果還在載入中，等待
    if (authLoading || merchantLoading) {
      console.log('⏳ 等待載入完成...');
      return;
    }

    // 載入商戶資料
    if (merchantSlug && !merchant && !merchantError) {
      console.log('📋 載入商戶資料:', merchantSlug);
      fetchMerchant(merchantSlug);
      return;
    }

    // 檢查商戶是否存在
    if (merchantSlug && merchantError) {
      console.error('❌ 商戶不存在:', merchantSlug);
      setStatus('error');
      return;
    }

    // 如果已經登入，重定向到適當頁面
    if (isAuthenticated && user) {
      console.log('✅ 用戶已登入，重定向到 profile');
      const redirectTo = merchantSlug ? `/${merchantSlug}/profile` : '/profile';
      
      // 儲存重定向資訊到 sessionStorage（以防需要）
      sessionStorage.setItem('line_direct_login_success', 'true');
      
      setTimeout(() => {
        navigate(redirectTo);
      }, 1000);
      return;
    }

    // 如果沒有登入，直接重定向到 LINE 授權
    if (!isAuthenticated && !authLoading) {
      console.log('🔐 用戶未登入，重定向到 LINE 授權');
      setStatus('redirecting');
      
      // 驗證 LINE 配置
      if (!authService.validateLineConfig()) {
        console.error('❌ LINE 配置無效');
        setStatus('error');
        return;
      }

      // 生成 LINE 授權 URL
      try {
        const lineAuthUrl = authService.generateLineAuthUrl(merchantSlug);
        console.log('🔗 重定向到 LINE:', lineAuthUrl);
        
        // 儲存當前路徑以便登入後返回
        const returnPath = merchantSlug ? `/${merchantSlug}/profile` : '/profile';
        sessionStorage.setItem('auth_redirect', returnPath);
        
        // 短暫延遲後重定向，讓用戶看到載入狀態
        setTimeout(() => {
          window.location.href = lineAuthUrl;
        }, 1500);
        
      } catch (error) {
        console.error('❌ 生成 LINE 授權 URL 失敗:', error);
        setStatus('error');
      }
    }
  }, [merchantSlug, isAuthenticated, authLoading, user, merchant, merchantLoading, merchantError, fetchMerchant, navigate]);

  const renderContent = () => {
    switch (status) {
      case 'checking':
        return (
          <div className="text-center">
            <LoadingSpinner size="large" />
            <h2 className="mt-4 text-xl font-semibold text-gray-900">
              檢查登入狀態...
            </h2>
            <p className="mt-2 text-gray-600">
              正在驗證您的登入資訊
            </p>
          </div>
        );

      case 'redirecting':
        return (
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 mb-4">
              <svg
                className="h-6 w-6 text-blue-600 animate-spin"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-gray-900">
              正在重定向到 LINE 登入...
            </h2>
            <p className="mt-2 text-gray-600">
              請稍候，我們正在為您準備 LINE 登入頁面
            </p>
            {merchant && (
              <p className="mt-2 text-sm text-blue-600">
                登入後將進入 {merchant.name}
              </p>
            )}
          </div>
        );

      case 'error':
        return (
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
              <svg
                className="h-6 w-6 text-red-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-gray-900">
              登入失敗
            </h2>
            <p className="mt-2 text-gray-600">
              {merchantError ? '商戶不存在或已停用' : 'LINE 登入配置錯誤'}
            </p>
            <div className="mt-4 space-x-4">
              <button
                onClick={() => window.location.reload()}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
              >
                重試
              </button>
              <button
                onClick={() => navigate(merchantSlug ? `/${merchantSlug}` : '/')}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                返回首頁
              </button>
            </div>
          </div>
        );

      default:
        return (
          <div className="text-center">
            <LoadingSpinner size="large" />
            <h2 className="mt-4 text-xl font-semibold text-gray-900">
              處理中...
            </h2>
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="bg-white shadow-md rounded-lg p-8">
          {/* Header */}
          <div className="text-center mb-6">
            {merchant ? (
              <>
                <h1 className="text-2xl font-bold text-gray-900 mb-2">
                  {merchant.name}
                </h1>
                <p className="text-gray-600">
                  LINE 快速登入
                </p>
              </>
            ) : (
              <>
                <h1 className="text-2xl font-bold text-gray-900 mb-2">
                  LINE 登入
                </h1>
                <p className="text-gray-600">
                  正在準備登入...
                </p>
              </>
            )}
          </div>

          {/* Content */}
          {renderContent()}

          {/* Debug Information (only in development or with debug=true) */}
          {(process.env.NODE_ENV === 'development' || window.location.search.includes('debug=true')) && (
            <div className="mt-6 bg-blue-50 border border-blue-200 rounded-md p-4">
              <h3 className="text-sm font-medium text-blue-800 mb-2">調試資訊</h3>
              <div className="text-xs text-blue-700 space-y-1">
                <div>商戶: {debugInfo.merchantSlug || 'N/A'}</div>
                <div>已認證: {debugInfo.isAuthenticated ? '是' : '否'}</div>
                <div>載入中: {debugInfo.authLoading ? '是' : '否'}</div>
                <div>用戶: {debugInfo.user?.name || 'N/A'}</div>
                <div>商戶資料: {debugInfo.merchant?.name || 'N/A'}</div>
                <div>狀態: {status}</div>
                <div className="text-xs text-blue-600 mt-2">
                  {debugInfo.timestamp}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Instructions */}
        <div className="bg-white shadow-md rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            LINE 直接登入說明
          </h3>
          <ul className="text-sm text-gray-600 space-y-2">
            <li className="flex items-start">
              <span className="flex-shrink-0 h-5 w-5 text-green-500 mr-2">✓</span>
              自動檢查登入狀態
            </li>
            <li className="flex items-start">
              <span className="flex-shrink-0 h-5 w-5 text-green-500 mr-2">✓</span>
              未登入時自動重定向到 LINE 授權
            </li>
            <li className="flex items-start">
              <span className="flex-shrink-0 h-5 w-5 text-green-500 mr-2">✓</span>
              已登入時直接進入系統
            </li>
            <li className="flex items-start">
              <span className="flex-shrink-0 h-5 w-5 text-green-500 mr-2">✓</span>
              支援商戶專屬登入
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default LineDirectLogin;